import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import { buildSearchIndex, searchSuggest, buildHierarchyIndex, suggestHierarchy } from './searchApi';
import UpgradePrompt from './UpgradePrompt';

// ============================================================================
// SearchBar — lexical typeahead used across the site (Home, Library, video page,
// results page). One behavior, several looks via `variant`.
//
// The dropdown animates organically: it stays mounted and its open state drives a
// height + fade + slide transition, so the panel grows to fit its content rather
// than popping. While a query is still resolving we show a soft shimmer (never a
// premature "No matches"); rows fade in gently as they arrive.
// ============================================================================

const MAX_PANEL = 440; // px cap for the dropdown height; longer lists scroll, the commit row stays pinned

// DROPDOWN GROUPS. Results are one flat list in display order — topics, then
// sections, chapters, courses (narrowest to broadest) — each item tagged with its
// `kind`. A group with no matches simply has no items, so it never shows. Keeping
// one flat list means the keyboard, shimmer, empty state and cache all work on a
// plain array exactly as before; only rendering and clicking look at `kind`.
const NODE_LIMIT = 3;   // max sections, chapters and courses each (topics: maxResults)
const GROUP_LABEL = { topic: 'Topics', section: 'Sections', chapter: 'Chapters', course: 'Courses' };

// ============================================================================
// UPLOAD — punchlist 1.3
// ============================================================================

// WHO SEES THE PAPERCLIP. An explicit allow-list, not a rank threshold, because
// this list is expected to change and "who is allowed" is the actual question.
// Same shape as PANEL_TIERS / ANSWER_TIERS in InterpretationPanel.jsx, and as
// MATH_TIERS below.
//
// LATER: this opens to everyone. Until then pro_plus only.
const UPLOAD_TIERS = ['pro_plus'];

// WHO SEES THE MATH TOGGLE — and with it the math row. Same reasoning as
// UPLOAD_TIERS: an allow-list, because the list will change. Hidden on free and
// pro while the math input is unfinished (nothing typed there is sent yet).
const MATH_TIERS = ['pro_plus'];

// What the picker will offer. A worksheet is a photo or a scan.
// EXPLICIT TYPES. NOT `image/*`, AND NEVER `image/heic`.
//
// iOS Safari transcodes a picked photo into whatever this list allows. With
// `image/*` the wildcard INCLUDES HEIC, so Safari hands over the raw HEIC and
// the server rejects it — which is why a screenshot uploaded fine (PNG) and a
// camera photo did not.
//
// Naming jpeg/png/webp makes Safari convert on the way out, and no HEIC ever
// reaches us. The server cannot decode it: sharp's prebuilt binaries have no
// HEIC support, and enabling it needs libvips built with libheif plus an HEVC
// patent licence. See the header of search-server/src/services/normalise.js.
//
// DO NOT ADD image/heic TO "BE HELPFUL". On Safari 17+ its presence inverts the
// behaviour and Safari starts converting JPEG and PNG uploads INTO HEIC.
//
// `application/pdf` is separate and safe — it only widens the picker to Files.
const UPLOAD_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';

// Client-side pacing between asks.
//
// OFF. Set to 0 to disable — every guard below reads this, so one number turns
// it on again.
//
// It was 60s, which made testing miserable and was never the real protection:
// the SERVER rate limit is 60/min and that is the one that matters. This was
// only ever there to stop a student firing three uploads in ten seconds, and it
// cost far more than it was worth.
//
// If it comes back, it should be seconds rather than a minute, and it should
// key on the request FINISHING rather than on the moment of navigation — see
// the undecided delay note in 1.3.
const ASK_COOLDOWN_MS = 0;
let lastAskAt = 0;

// ── THE FILE HANDOFF ────────────────────────────────────────────────────────
//
// THE FILE DOES NOT TRAVEL IN `location.state`.
//
// It used to. React Router puts location state into `history.pushState`, which
// STRUCTURED-CLONES it — a File included, bytes and all. WebKit caps serialised
// history state at roughly 2MB and throws above it; the other engines have
// their own limits. A worksheet PDF at a few hundred KB survived that; a phone
// screenshot at 1-3MB and a camera photo at 2-8MB did not, which is exactly the
// split that showed up in testing. A throwing pushState propagates straight out
// of the click handler.
//
// So the file is handed over in a module variable and `location.state` carries
// a one-word marker. Nothing large is ever serialised.
//
// SAME LIFETIME AS BEFORE: a module variable dies on a refresh, which is what
// `?q=` already exists to cover. It also means a BACK navigation finds nothing
// rather than re-firing the upload — an improvement, since history state would
// have replayed it.
let pendingUpload = null;

/**
 * Read the handed-over file and clear it. Read-once on purpose: the upload
 * belongs to the navigation that created it, and a second reader would be a
 * second submission of the same page.
 */
export function takePendingUpload() {
  const held = pendingUpload;
  pendingUpload = null;
  console.info('[searchbar] handoff taken', held ? held.file.name : 'EMPTY');
  return held;
}

// A function glyph, not a keyboard glyph. The button turns MATH INPUT on; a
// keyboard rising is a consequence of that, not the thing being asked for.
const MathIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 19c1.5 0 2.2-.8 2.5-2.4L8.8 5.9C9.1 4.5 9.9 4 11 4M6 11h6M14 10l6 8M20 10l-6 8"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClipIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M21.44 11.05l-8.49 8.49a5 5 0 0 1-7.07-7.07l8.49-8.49a3.5 3.5 0 0 1 4.95 4.95l-8.49 8.49a2 2 0 0 1-2.83-2.83l7.78-7.78"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

const SearchIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke={color} strokeWidth="2" />
    <line x1="16.5" y1="16.5" x2="21" y2="21" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const LockIcon = ({ size = 12, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" stroke={color} strokeWidth="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke={color} strokeWidth="2" />
  </svg>
);

// Small mark for section / chapter / course rows, in place of a thumbnail.
const LEVEL_PATHS = {
  course: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /></>,
  chapter: <path d="M4 6h16M4 12h16M4 18h10" />,
  section: <><path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4.5" cy="6" r="1" /><circle cx="4.5" cy="12" r="1" /><circle cx="4.5" cy="18" r="1" /></>,
};
const LevelIcon = ({ kind, color }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {LEVEL_PATHS[kind]}
  </svg>
);

// EVERY PRESET CARRIES THE SAME CONTROLS: left icon, math toggle, paperclip,
// send. Only the METRICS vary — radius, padding, font size, background. The math
// toggle and the paperclip are TIER-GATED (MATH_TIERS, UPLOAD_TIERS), and every
// preset gates them the same way.
//
// There is NO clear button. Backspace clears text. The `×` that exists belongs
// to an ATTACHED FILE, which cannot be removed any other way.
//
// It used to be otherwise: `library`, `default` and `compact` had no send
// button at all, and `home` had no left icon. That was invisible while the right
// side of those bars was empty. Adding the paperclip made it obvious — a lone
// paperclip where a submit button belongs reads as a submit button that went
// missing. A student should not have to learn which page they are on to know
// what the search bar can do.
//
// `home` keeps the UP arrow rather than the right arrow; it still has a send
// button, it just points differently on the hero. Change it here if that should
// match too.
const PRESETS = {
  default:   { radius: 16, padding: '13px 18px',            font: 16, leftIcon: true, send: 'right', focusRing: true,  bg: 'card' },
  compact:   { radius: 12, padding: '8px 12px',             font: 14, leftIcon: true, send: 'right', focusRing: true,  bg: 'card' },
  home:      { radius: 26, padding: '13px 14px 13px 24px',  font: 17, leftIcon: true, send: 'up',    focusRing: false, bg: 'surface' },
  video:     { radius: 26, padding: '8px 8px 8px 18px',     font: 15, leftIcon: true, send: 'right', focusRing: true,  bg: 'card' },
  library:   { radius: 14, padding: '16px 20px',            font: 15, leftIcon: true, send: 'right', focusRing: true,  bg: 'surface' },
  prominent: { radius: 30, padding: '18px 24px',            font: 18, leftIcon: true, send: 'right', focusRing: true,  bg: 'surface', rest: true },
};

function highlightTitle(title, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return title;
  const idx = title.toLowerCase().indexOf(q);
  if (idx === -1) return title;
  return (
    <>
      {title.slice(0, idx)}
      <strong style={{ fontWeight: 700 }}>{title.slice(idx, idx + q.length)}</strong>
      {title.slice(idx + q.length)}
    </>
  );
}

function secondaryLine(item) {
  if (item.matchType === 'description' || item.matchType === 'tag') return item.snippet;
  return item.series;
}

export default function SearchBar({
  variant = 'default',
  placeholder = 'Search lessons…',
  initialValue = '',
  autoFocus = false,
  maxResults = 5,        // max topics in the dropdown
  style,
  // TRUE while the results page has a request in flight. Only that page knows,
  // so only that page passes it — everywhere else the default holds and the
  // paperclip is live. Wired in 1.6.
  uploadBusy = false,
}) {
  const { catalog, profile } = useAuth();
  const currentTier = profile?.currentTier || 'free';
  const { colors, isLight } = useTheme();
  const navigate = useNavigate();
  const cfg = PRESETS[variant] || PRESETS.default;

  const index = useMemo(() => buildSearchIndex(catalog), [catalog]);
  const hIndex = useMemo(() => buildHierarchyIndex(catalog), [catalog]);

  const [value, setValue] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('idle');   // idle | loading | ready
  const [open, setOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(-1);
  const [panelH, setPanelH] = useState(0);

  // Upload state. DECLARED HERE, with the rest — `goToResults` closes over
  // `file` and lists it as a dependency, and a `const` declared below that
  // callback is in the temporal dead zone when the dependency array is
  // evaluated during render. That throws before anything paints.
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);

  // MATH INPUT. Off by default, every time — deliberately not persisted. A
  // student who wants it says so; nobody arrives at a text search wondering why
  // there is a second field.
  const [mathOn, setMathOn] = useState(false);
  // Tracked so the row can show it has content. Not sent anywhere yet.
  const [mathLatex, setMathLatex] = useState('');
  const mathRef = useRef(null);

  const [cooling, setCooling] = useState(false);

  // One cache per catalog + plan. A new Map whenever either changes means a cached
  // result can never carry locks from a previous plan (e.g. right after upgrading).
  const cache = useMemo(() => new Map(), [index, hIndex, currentTier]);
  const reqRef = useRef(0);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);
  const contentRef = useRef(null);
  const commitRef = useRef(null);
  const shownRef = useRef({ results: [], status: 'idle', query: '' });

  const runSuggest = useCallback((raw) => {
    const query = (raw || '').trim();
    if (!query) { setResults([]); setStatus('idle'); return; }
    if (cache.has(query)) { setResults(cache.get(query)); setStatus('ready'); return; }
    const reqId = ++reqRef.current;
    // TODO(backend): const r = await searchSuggestRemote(query, { limit: maxResults });
    const topics = searchSuggest(query, index, { limit: maxResults, currentTier }).map((t) => ({ ...t, kind: 'topic' }));
    const h = suggestHierarchy(query, hIndex, { limit: NODE_LIMIT, currentTier });
    const r = [...topics, ...h.sections, ...h.chapters, ...h.courses];
    if (reqId !== reqRef.current) return;
    cache.set(query, r);
    setResults(r);
    setStatus('ready');
  }, [index, hIndex, cache, maxResults, currentTier]);

  // Debounce keystrokes. Cached queries resolve instantly; uncached ones enter a
  // 'loading' status (we keep showing prior results, or a shimmer if there are none)
  // so the panel never flashes an empty state before results arrive.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = value.trim();
    if (!q) { setResults([]); setStatus('idle'); return undefined; }
    if (cache.has(q)) { runSuggest(q); return undefined; }
    setStatus('loading');
    debounceRef.current = setTimeout(() => runSuggest(q), 180);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value, cache, runSuggest]);


  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Keep the keyboard-highlighted row visible. Scrolls only the panel (never the
  // page), and treats the pinned "Search for …" row as covering the bottom edge.
  useEffect(() => {
    const box = contentRef.current;
    if (!box || active < 0) return;
    const row = box.querySelector('[data-idx="' + active + '"]');
    if (!row) return;
    const pinned = commitRef.current ? commitRef.current.offsetHeight : 0;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < box.scrollTop) box.scrollTop = top;
    else if (bottom > box.scrollTop + box.clientHeight - pinned) box.scrollTop = bottom - box.clientHeight + pinned;
  }, [active]);

  // Measure content height every render (idempotent) so the panel can animate its
  // height to fit — this is what makes it grow organically instead of snapping.
  useLayoutEffect(() => {
    if (contentRef.current) setPanelH(contentRef.current.scrollHeight);
  });

  // Snapshot the content while the panel is open, so when it closes we keep
  // rendering that last state during the collapse — the panel fades/rolls up with
  // its results intact instead of swapping to a shimmer or "no matches".
  useEffect(() => {
    if (open && value.trim().length > 0) shownRef.current = { results, status, query: value.trim() };
  }, [open, results, status, value]);

  // Click or Enter on a dropdown row. Topics play (or, when locked, open the
  // upgrade prompt). Sections, chapters and courses always navigate to their place
  // on the Courses page, locked or not — browsing is never blocked.
  const openItem = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    if (item.kind !== 'topic') { navigate(item.path); return; }
    if (item.locked) { setUpgradeOpen(true); return; }
    navigate('/video/' + item.videoId + (item.courseId ? '?course=' + item.courseId : ''));
  }, [navigate]);

  const goToResults = useCallback(() => {
    const q = value.trim();

    // WITH A FILE: hand it to the results page, which fires everything on mount
    // — exactly as it would if the upload had happened there. One code path,
    // and SearchBar stays what it already is.
    //
    // The file is cleared the instant it is SUBMITTED. Held longer it becomes a
    // second copy of state that can disagree with the log.
    if (file) {
      setOpen(false);
      lastAskAt = Date.now();
      if (ASK_COOLDOWN_MS) setCooling(true);
      setFile(null);

      // The file goes in the module variable; the route carries only a marker.
      // `{ upload: true }` is a few bytes and always serialises.
      pendingUpload = { file, text: q };
      console.info('[searchbar] handing off', { name: file.name, type: file.type, bytes: file.size });
      navigate('/search', { state: { upload: true } });
      return;
    }

    // WITHOUT ONE: unchanged. Lexical search, ?q= in the URL, survives refresh.
    if (!q) return;
    setOpen(false);
    navigate('/search?q=' + encodeURIComponent(q));
  }, [value, file, navigate]);

  // ── UPLOAD ───────────────────────────────────────────────────────────────


  const canUpload = UPLOAD_TIERS.includes(currentTier);
  const canMath = MATH_TIERS.includes(currentTier);
  const uploadDisabled = uploadBusy || cooling;

  // Tick the cooldown down so the button re-enables on its own. Only runs while
  // actually cooling, so there is no timer sitting on every page.
  useEffect(() => {
    if (!cooling) return undefined;
    if (!ASK_COOLDOWN_MS) { setCooling(false); return undefined; }
    const remaining = ASK_COOLDOWN_MS - (Date.now() - lastAskAt);
    if (remaining <= 0) { setCooling(false); return undefined; }
    const t = setTimeout(() => setCooling(false), remaining);
    return () => clearTimeout(t);
  }, [cooling]);

  // On mount, pick up a cooldown started by a DIFFERENT SearchBar instance —
  // the student uploads from Home, lands on the results page, and that page's
  // bar has to know it is still cooling.
  useEffect(() => {
    if (ASK_COOLDOWN_MS && Date.now() - lastAskAt < ASK_COOLDOWN_MS) setCooling(true);
  }, []);

  // ATTACHES A FILE. IT UPLOADS NOTHING, AND IT DOES NOT NAVIGATE.
  //
  // Picking shows the file as a chip so the student can see what is going and
  // type alongside it. SEND is what leaves the page — which is also what makes
  // `{ file, text }` coherent, since the text is whatever they typed while the
  // file was attached rather than whatever happened to be there when they
  // picked it.
  const onUpload = useCallback((e) => {
    const picked = e.target.files && e.target.files[0];

    // Reset the input immediately, or picking the same file twice in a row does
    // nothing — the element only fires on CHANGE.
    e.target.value = '';

    if (!picked) return;
    setFile(picked);
    setOpen(false);
  }, []);

  // The field is created when the toggle goes on, so this runs then.
  //
  // NOTHING IS WIRED TO A KEYBOARD. The custom virtual keyboard was removed and
  // will be rebuilt from scratch; MathLive's own default applies, which means a
  // keyboard on touch and none on a desktop. This only tracks what is typed so
  // the row can show it has content.
  useEffect(() => {
    const mf = mathRef.current;
    if (!mathOn || !mf) return undefined;

    const onInput = () => setMathLatex(mf.value);
    mf.addEventListener('input', onInput);
    mf.focus();

    return () => mf.removeEventListener('input', onInput);
  }, [mathOn]);

  // Off means gone: the field and its content.
  const toggleMath = useCallback(() => {
    setMathOn((on) => {
      if (on) setMathLatex('');
      return !on;
    });
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0 && results[active]) openItem(results[active]);
      else goToResults();
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  const secondaryColor = colors.textSecondary || colors.muted || '#888';
  const panelBg = colors.cardBg || colors.bgAlt || '#1A1A1A';
  const baseBorder = isLight ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.12)';
  const activeBg = isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.07)';
  // While a request is in flight, the placeholder says so — that a search is on
  // its way, that editing means waiting, and that typing here starts something
  // new rather than adding to what is already running.
  const shownPlaceholder = uploadBusy
    ? 'Reading your page — editing means waiting. Typing here starts something new.'
    : placeholder;

  const showPanel = open && value.trim().length > 0;
  const disp = showPanel ? { results, status, query: value.trim() } : shownRef.current;
  const hot = focused || showPanel;

  const inputBg = cfg.bg === 'card'
    ? (isLight ? '#FFFFFF' : (colors.cardBg || '#1A1A1A'))
    : (isLight ? '#FFFFFF' : (variant === 'library' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.06)'));

  const borderColor = hot ? (cfg.focusRing ? (colors.accent || baseBorder) : baseBorder) : baseBorder;
  const restShadow = cfg.rest
    ? (isLight ? '0 1px 6px rgba(0,0,0,0.08), 0 10px 28px -14px rgba(0,0,0,0.22)' : '0 10px 28px -14px rgba(0,0,0,0.55)')
    : 'none';
  const focusShadow = hot
    ? (cfg.focusRing ? `0 0 0 3px ${colors.accentGlow || 'rgba(0,0,0,0.04)'}` : (isLight ? '0 2px 12px rgba(0,0,0,0.08)' : '0 2px 12px rgba(0,0,0,0.3)'))
    : restShadow;
  const r = cfg.radius;
  const inputRadius = showPanel ? `${r}px ${r}px 0 0` : `${r}px`;
  const sendActive = value.trim().length > 0 || !!file;
  const sendColor = isLight ? '#B8A088' : '#88C9B2';

  const showShimmer = disp.results.length === 0 && disp.status === 'loading';
  const showEmpty = disp.results.length === 0 && disp.status === 'ready';
  const shimmerBg = isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)';

  return (
    <div ref={rootRef} style={{ position: 'relative', width: '100%', fontFamily: '"DM Sans", sans-serif', ...style }}>
      {/* ONE SHELL, TWO ROWS. The border, background, radius and focus ring all
          live HERE rather than on the rows, so the maths field is a second
          storey of the same control instead of a separate bar floating beneath
          it. The rows carry only their own padding and a hairline between. */}
      <div
        style={{
          background: inputBg,
          border: `1px solid ${borderColor}`,
          borderRadius: inputRadius,
          transition: 'border-color 0.25s ease, box-shadow 0.25s ease, border-radius 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          boxShadow: focusShadow,
          overflow: 'hidden',
        }}
      >
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: cfg.padding,
        }}
      >
        {cfg.leftIcon && <SearchIcon size={cfg.font + 1} color={secondaryColor} />}

        {/* THE ATTACHED FILE.
            Inside the bar, before the text, so it reads as part of what is
            about to be sent rather than as a notice about it. Its `×` is the
            ONLY × in the bar — a file cannot be removed any other way, whereas
            text has backspace. Capped and ellipsised so a long filename cannot
            squeeze the input. */}
        {file && (
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              flexShrink: 0, maxWidth: '45%',
              padding: '4px 6px 4px 10px',
              marginRight: '8px',
              borderRadius: '999px',
              background: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.09)',
              fontSize: Math.max(12, cfg.font - 3),
              color: colors.text,
              lineHeight: 1.4,
            }}
          >
            <ClipIcon size={Math.max(12, cfg.font - 4)} color={secondaryColor} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {file.name}
            </span>
            <button
              type="button"
              onClick={() => setFile(null)}
              aria-label={`Remove ${file.name}`}
              style={{
                flexShrink: 0, width: '18px', height: '18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', background: 'transparent', padding: 0,
                borderRadius: '50%', cursor: 'pointer',
                color: secondaryColor, fontSize: '15px', lineHeight: 1,
              }}
            >
              ×
            </button>
          </span>
        )}
        <input
          type="text"
          value={value}
          placeholder={shownPlaceholder}
          autoFocus={autoFocus}
          onChange={(e) => { setValue(e.target.value); setActive(-1); setOpen(true); }}
          onFocus={() => { setFocused(true); setOpen(true); }}
          onBlur={() => setFocused(false)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="searchbar-listbox"
          aria-autocomplete="list"
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            color: colors.text, fontSize: cfg.font, fontFamily: 'inherit', minWidth: 0,
          }}
        />
        {/* MATH INPUT TOGGLE. Left of the paperclip, so the two things that
            change what gets SENT sit together, before the send button.
            MATH_TIERS only. */}
        {canMath && (
        <button
          type="button"
          onClick={toggleMath}
          aria-pressed={mathOn}
          aria-label={mathOn ? 'Turn off math input' : 'Turn on math input'}
          title={mathOn ? 'Turn off math input' : 'Math input'}
          style={{
            width: '32px', height: '32px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: 'none', padding: 0, borderRadius: '50%', cursor: 'pointer',
            background: mathOn ? (colors.accentGlow || 'rgba(255,255,255,0.10)') : 'transparent',
            color: mathOn ? colors.accent : secondaryColor,
            transition: 'background 0.2s ease',
          }}
        >
          <MathIcon size={cfg.font + 2} color={mathOn ? colors.accent : secondaryColor} />
        </button>
        )}

        {canUpload && (
          <>
            {/* VISUALLY HIDDEN, NOT `display: none`.
                A `display: none` input cannot be opened by `.click()` on iOS
                Safari — the call returns silently and no picker appears, which
                is exactly the symptom on mobile. It has to remain in the layout
                and be hidden by other means. */}
            <input
              ref={fileRef}
              type="file"
              accept={UPLOAD_ACCEPT}
              onChange={onUpload}
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
                border: 0,
                opacity: 0,
              }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={uploadDisabled}
              aria-label={
                uploadBusy ? 'Reading your page' :
                cooling ? 'Wait a moment before sending another page' :
                'Upload a page'
              }
              title={
                uploadBusy ? 'Reading your page' :
                cooling ? 'Wait a moment before sending another page' :
                'Upload a page'
              }
              style={{
                width: '32px', height: '32px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', background: 'transparent', padding: 0,
                borderRadius: '50%',
                cursor: uploadDisabled ? 'default' : 'pointer',
                opacity: uploadDisabled ? 0.35 : 1,
                transition: 'opacity 0.25s ease',
              }}
            >
              <ClipIcon size={cfg.font + 2} color={secondaryColor} />
            </button>
          </>
        )}
        {cfg.send && (
          <button type="button" onClick={goToResults} aria-label="Search"
            style={{
              width: variant === 'home' ? '36px' : '38px', height: variant === 'home' ? '36px' : '38px',
              borderRadius: '50%', flexShrink: 0, border: 'none', cursor: sendActive ? 'pointer' : 'default',
              background: sendActive ? sendColor : (isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)'),
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.25s ease',
              opacity: sendActive ? 1 : 0.6,
            }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={sendActive ? '#FFFFFF' : secondaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {cfg.send === 'up' ? <path d="M12 19V5M5 12l7-7 7 7" /> : <path d="M5 12h14M13 6l6 6-6 6" />}
            </svg>
          </button>
        )}
      </div>


      {/* ── THE MATH FIELD ────────────────────────────────────────────────────
          A SECOND ROW of the same control, not a second control. No border of
          its own beyond the hairline dividing the two rows; the shell provides
          everything else.

          NOT WIRED TO ANYTHING YET. What is typed here is not sent — the
          keyboard and the submit path were removed together and will be rebuilt
          from scratch. */}
      {/* canMath too, not just mathOn: if the tier drops while the row is open,
          the row goes with the button rather than being left with no way to
          close it except its own ×. */}
      {canMath && mathOn && (
        <div
          className="sb-math"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: cfg.padding,
            borderTop: `1px solid ${mathLatex.trim() ? (colors.accent || baseBorder) : baseBorder}`,
            transition: 'border-color 0.25s ease',
          }}
        >
          <span style={{ fontSize: '11px', color: secondaryColor, flexShrink: 0,
            letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            math
          </span>
          <math-field
            ref={mathRef}
            style={{
              flex: 1, minWidth: 0,
              fontSize: `${cfg.font + 3}px`,
              border: 'none', background: 'transparent', padding: 0,
              color: colors.text,
            }}
          />
          <button
            type="button"
            onClick={toggleMath}
            aria-label="Remove math input"
            style={{
              flexShrink: 0, width: '24px', height: '24px', border: 'none',
              background: 'transparent', color: secondaryColor, cursor: 'pointer',
              fontSize: '17px', lineHeight: 1, padding: 0,
            }}
          >
            ×
          </button>
        </div>
      )}
      </div>

      <div id="searchbar-listbox" role="listbox" aria-hidden={!showPanel}
        style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: panelBg,
          // Three sides spelled out, no top: mixing the `border` shorthand with
          // `borderTop` lets React re-add a top border when the panel toggles.
          borderLeft: `1px solid ${showPanel ? (colors.accent || baseBorder) : 'transparent'}`,
          borderRight: `1px solid ${showPanel ? (colors.accent || baseBorder) : 'transparent'}`,
          borderBottom: `1px solid ${showPanel ? (colors.accent || baseBorder) : 'transparent'}`,
          borderRadius: `0 0 ${Math.min(r, 16)}px ${Math.min(r, 16)}px`,
          boxShadow: showPanel ? '0 18px 44px rgba(0,0,0,0.30)' : '0 18px 44px rgba(0,0,0,0)',
          overflow: 'hidden',
          height: showPanel ? `${Math.min(panelH, MAX_PANEL)}px` : '0px',
          opacity: showPanel ? 1 : 0,
          transform: showPanel ? 'translateY(0)' : 'translateY(-6px)',
          transformOrigin: 'top center',
          pointerEvents: showPanel ? 'auto' : 'none',
          transition: 'height 0.42s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease, transform 0.42s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.42s ease, border-color 0.3s ease',
        }}>
        <div ref={contentRef} style={{ position: 'relative', maxHeight: `${MAX_PANEL}px`, overflowY: 'auto' }}>
          {showShimmer && [0, 1, 2].map((k) => (
            <div key={`s${k}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px' }}>
              <div className="sb-pulse" style={{ width: '48px', height: '27px', borderRadius: '4px', flexShrink: 0, background: shimmerBg }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="sb-pulse" style={{ height: '10px', width: `${60 - k * 8}%`, borderRadius: '4px', background: shimmerBg, marginBottom: '7px' }} />
                <div className="sb-pulse" style={{ height: '9px', width: `${38 - k * 6}%`, borderRadius: '4px', background: shimmerBg }} />
              </div>
            </div>
          ))}

          {disp.results.map((item, i) => {
            // A group header wherever the kind changes. Headers are not options, so
            // the keyboard index (i) only ever counts rows.
            const header = i === 0 || disp.results[i - 1].kind !== item.kind;
            const key = item.kind === 'topic' ? 'topic:' + item.videoId : item.kind + ':' + item.id;
            return (
              <React.Fragment key={key}>
                {header && (
                  <div role="presentation" style={{
                    padding: i === 0 ? '10px 16px 4px' : '12px 16px 4px', color: secondaryColor,
                    fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
                  }}>
                    {GROUP_LABEL[item.kind]}
                  </div>
                )}
                <div data-idx={i} className="sb-row" role="option" aria-selected={i === active}
                  onMouseEnter={() => setActive(i)} onClick={() => openItem(item)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 16px', cursor: 'pointer',
                    background: i === active ? activeBg : 'transparent', transition: 'background 0.18s ease',
                  }}>
                  <div style={{ width: '48px', height: '27px', borderRadius: '4px', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)' }}>
                    {item.kind === 'topic'
                      ? (item.thumbnailUrl && <img src={item.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />)
                      : <LevelIcon kind={item.kind} color={secondaryColor} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: colors.text, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {highlightTitle(item.title, disp.query)}
                    </div>
                    <div style={{ color: secondaryColor, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.kind === 'topic' ? secondaryLine(item) : item.crumb}
                    </div>
                  </div>
                  {item.locked && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: secondaryColor, fontSize: '11px', flexShrink: 0 }}>
                      <LockIcon size={12} color={secondaryColor} /> Locked
                    </span>
                  )}
                </div>
              </React.Fragment>
            );
          })}

          {showEmpty && (
            <div className="sb-row" style={{ padding: '18px', color: secondaryColor, fontSize: '15px', fontStyle: 'italic', fontFamily: '"Cormorant Garamond", serif', textAlign: 'center' }}>
              Nothing in your catalog matches that yet
            </div>
          )}

          <div ref={commitRef} className="sb-row" role="option" aria-selected={active === -1} onClick={goToResults} onMouseEnter={() => setActive(-1)}
            style={{
              // Pinned to the bottom so "Search for …" is always visible, however
              // long the list above it. Solid panel colour underneath the highlight.
              position: 'sticky', bottom: 0, zIndex: 1, backgroundColor: panelBg,
              backgroundImage: active === -1 ? `linear-gradient(${activeBg}, ${activeBg})` : 'none',
              display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 16px', cursor: 'pointer',
              borderTop: `1px solid ${baseBorder}`,
              color: colors.accent || colors.text, fontSize: '13px', fontWeight: 600, transition: 'background 0.18s ease',
            }}>
            <SearchIcon size={15} color={colors.accent || colors.text} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Search for “{disp.query}”</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes sbPulse { 0%, 100% { opacity: 0.45; } 50% { opacity: 0.9; } }
        .sb-pulse { animation: sbPulse 1.6s ease-in-out infinite; }
        @keyframes sbRowIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: none; } }
        .sb-row { animation: sbRowIn 0.34s cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes sbMathIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
        .sb-math { animation: sbMathIn 0.25s cubic-bezier(0.22, 1, 0.36, 1) both; }
        @media (prefers-reduced-motion: reduce) {
          .sb-pulse, .sb-row, .sb-math { animation: none; }
        }
      `}</style>

      <UpgradePrompt open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}
